import React, { useMemo } from 'react';
import { useWindowSize } from 'react-use';

import Card from '@mui/material/Card';

import { useTranslation } from 'next-i18next';

import { getUnorderedListColumns } from 'src/utils/getUnorderedListColumns';
import theme from 'src/utils/theme';
import isNotNull from 'src/utils/isNotNull';
import { breakpoints } from 'src/constants/breakpoints';
import type { Ad } from 'src/types/__generated__/graphql';
import { styledComponent, styledTag } from 'src/utils/styled';
import type { AdDetailProps } from 'src/components/ad/AdDetail';
import AdDetail from 'src/components/ad/AdDetail';

const ResultCard = styledComponent(Card)`
    padding: 1rem;
    margin: 1rem 0;
    background-color: ${theme.palette.primary.main};
    color: ${theme.palette.primary.contrastText};
`;

ResultCard.defaultProps = {
    elevation: 0,
};

interface StyledUnorderedListProps {
    $cols?: number;
}
const StyledUnorderedList = styledTag('ul')<StyledUnorderedListProps>`
    padding-left: 0rem;
    display: grid;
    grid-template-columns: repeat(${(props) => props.$cols ?? 3}, auto);
    grid-column-gap: 1rem;
    grid-row-gap: 0.75rem;
`;

interface Props {
    ad: Ad;
    isOnEstimate?: boolean;
}

const Characteristics: React.FC<Props> = ({ ad, isOnEstimate }) => {
    const { t } = useTranslation(['create-ad', 'ad', 'common']);
    const { width } = useWindowSize();

    const { isMobile, isTabletPortait } = useMemo(
        () => ({
            isMobile: width < breakpoints.tabletPortrait,
            isTabletPortait: width >= breakpoints.tabletPortrait && width < breakpoints.tabletLandscape,
        }),
        [width],
    );

    const unorderedListCol = getUnorderedListColumns(isMobile, isTabletPortait);
    const characteristics: AdDetailProps[] = useMemo(
        () => [
            {
                leftText: t('ad:propertyType'),
                rightText: t(`ad:propertyTypes.${ad.propertyType}`),
                dataTestId: 'characteristics-typeProperty',
                condition: true,
            },
            {
                leftText: t('ad:furnishedText'),
                rightText: ad.furnished ? t('ad:yesNoNa.yes') : t('ad:yesNoNa.no'),
                dataTestId: 'characteristics-furnished',
                condition: isNotNull(ad.furnished) && !isOnEstimate,
            },
            {
                leftText: t('ad:constructionYear'),
                rightText: ad.constructionYear,
                dataTestId: 'characteristics-constructionYear',
                condition: isNotNull(ad.constructionYear),
            },
            {
                leftText: t('ad:livingAreaText'),
                rightText: `${ad.livingArea} m²`,
                dataTestId: 'characteristics-livingArea',
                condition: isNotNull(ad.livingArea),
            },
            {
                leftText: t(`ad:${ad.propertyType === 'apartment' ? 'landAreaApartment' : 'landArea'}`),
                rightText: `${ad.landArea} m²`,
                dataTestId: 'characteristics-landArea',
                condition: isNotNull(ad.landArea),
            },
            {
                leftText: t('ad:floorPosition'),
                rightText: ad.floorPosition,
                dataTestId: 'characteristics-floorPosition',
                condition: isNotNull(ad.floorPosition) && !isOnEstimate,
            },
            {
                leftText: t('ad:floorNumber'),
                rightText: ad.floorNumber,

                dataTestId: 'characteristics-floorNumber',
                condition: isNotNull(ad.floorNumber),
            },
            {
                leftText: t('ad:roomNumber'),
                rightText: ad.roomNumber,

                dataTestId: 'characteristics-roomNumber',
                condition: isNotNull(ad.roomNumber),
            },
            {
                leftText: t('ad:bedroomNumber'),
                rightText: ad.bedroomNumber,
                dataTestId: 'characteristics-bedroomNumber',
                condition: isNotNull(ad.bedroomNumber),
            },
            {
                leftText: t('ad:surfaceAreaText'),
                rightText: `${ad.surfaceArea} m²`,
                dataTestId: 'characteristics-surfaceArea',
                condition: isNotNull(ad.surfaceArea) && !isOnEstimate,
            },
            {
                leftText: t('ad:numberOfVehicles'),
                rightText: ad.numberOfVehicles,
                dataTestId: 'characteristics-numberOfVehicles',
                condition: isNotNull(ad.numberOfVehicles) && !isOnEstimate,
            },
            {
                leftText: t('ad:dimensions'),
                rightText: `${ad.dimensionWidth} x ${ad.dimensionLength} x ${ad.dimensionHeight}`,
                dataTestId: 'characteristics-dimensionWidth',
                condition: isNotNull(ad.dimensionWidth) && !isOnEstimate,
            },
            {
                leftText: t('ad:servicedLand'),
                rightText: t(`ad:yesNoNa.${ad.servicedLand}`),

                dataTestId: 'characteristics-servicedLand',
                condition: isNotNull(ad.servicedLand) && !isOnEstimate,
            },
            {
                leftText: t('ad:constructibleLand'),
                rightText: t(`ad:yesNoNa.${ad.constructibleLand}`),
                dataTestId: 'characteristics-constructibleLand',
                condition: isNotNull(ad.constructibleLand) && !isOnEstimate,
            },
            {
                leftText: t('ad:coOwnership'),
                rightText: ad.coOwnership ? t(`ad:yesNoNa.yes`) : t(`ad:yesNoNa.no`),
                dataTestId: 'characteristics-coOwnership',
                condition: isNotNull(ad.coOwnership) && !isOnEstimate,
            },
            {
                leftText: t('ad:coOwnershipNumberOfLots'),
                rightText: ad.coOwnershipNumberOfLots,

                dataTestId: 'characteristics-coOwnershipNumberOfLots',
                condition: isNotNull(ad.coOwnershipNumberOfLots) && !isOnEstimate,
            },
            {
                leftText: t('ad:coOwnershipOngoing'),
                rightText: t(`ad:yesNoNa.${ad.coOwnershipOngoing}`),
                dataTestId: 'characteristics-coOwnershipOngoing',
                condition: isNotNull(ad.coOwnershipOngoing) && !isOnEstimate,
            },
            {
                leftText: t('ad:coOwnershipYearlyCharge'),
                rightText: ad.coOwnershipYearlyCharge,
                dataTestId: 'characteristics-coOwnershipYearlyCharge',
                condition: isNotNull(ad.coOwnershipYearlyCharge) && !isOnEstimate,
            },
        ],
        [
            ad.bedroomNumber,
            ad.coOwnership,
            ad.coOwnershipNumberOfLots,
            ad.coOwnershipOngoing,
            ad.coOwnershipYearlyCharge,
            ad.constructibleLand,
            ad.constructionYear,
            ad.dimensionHeight,
            ad.dimensionLength,
            ad.dimensionWidth,
            ad.floorNumber,
            ad.floorPosition,
            ad.furnished,
            ad.landArea,
            ad.livingArea,
            ad.numberOfVehicles,
            ad.propertyType,
            ad.roomNumber,
            ad.servicedLand,
            ad.surfaceArea,
            isOnEstimate,
            t,
        ],
    );
    return <StyledUnorderedList $cols={unorderedListCol}>{characteristics.map(AdDetail)}</StyledUnorderedList>;
};

export default Characteristics;
